/**
 * Billing Page - SaaS Invoice Management
 * 
 * Complete invoice management interface:
 * - List invoices with filters
 * - Create/Edit invoices
 * - Status management
 * - Statistics dashboard
 * - Automatic invoice generation from subscriptions
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Plus, Search, Filter, RefreshCw, MoreVertical,
  Send, CreditCard, X, Clock, AlertTriangle, CheckCircle,
  Calendar, Building2, Download, Eye, Edit, Trash2, Bell,
  Zap, Play, Settings2, ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { api } from '../../App';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Status configuration
const INVOICE_STATUS = {
  DRAFT: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: Edit },
  SENT: { label: 'Envoyée', color: 'bg-blue-100 text-blue-700', icon: Send },
  PAID: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  OVERDUE: { label: 'En retard', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  CANCELLED: { label: 'Annulée', color: 'bg-gray-100 text-gray-500', icon: X }
};

const PAYMENT_METHODS = [
  { value: 'CARD', label: 'Carte bancaire' },
  { value: 'TRANSFER', label: 'Virement bancaire' },
  { value: 'CASH', label: 'Espèces' },
  { value: 'CHECK', label: 'Chèque' },
  { value: 'OTHER', label: 'Autre' }
];

const BillingPage = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('invoices');
  
  // Auto-billing state
  const [dueSubscriptions, setDueSubscriptions] = useState(null);
  const [loadingDue, setLoadingDue] = useState(false);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [generatingSingle, setGeneratingSingle] = useState(null);
  const [autoGenDialogOpen, setAutoGenDialogOpen] = useState(false);
  const [autoGenSettings, setAutoGenSettings] = useState({
    status: 'DRAFT',
    dry_run: true,
    vat_rate: 20,
    due_in_days: 30
  });
  const [autoGenResult, setAutoGenResult] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    account_id: '',
    issue_date: format(new Date(), 'yyyy-MM-dd'),
    due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    vat_rate: 20,
    currency: 'MAD',
    lines: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '',
    client_notes: ''
  });
  
  const [paymentData, setPaymentData] = useState({
    payment_method: 'TRANSFER',
    payment_reference: '',
    paid_date: format(new Date(), 'yyyy-MM-dd')
  });
  
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchInvoices();
    fetchAccounts();
    fetchDueSubscriptions();
  }, [statusFilter, accountFilter, page]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (accountFilter) params.append('account_id', accountFilter);
      if (search) params.append('search', search);
      params.append('page', page);
      params.append('limit', 20);
      
      const { data } = await api.get(`/admin/invoices?${params}`);
      setInvoices(data.invoices);
      setSummary(data.summary);
      setPagination(data.pagination);
    } catch (error) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get('/admin/accounts?limit=100');
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  // ============ AUTO-BILLING FUNCTIONS ============
  
  const fetchDueSubscriptions = async () => {
    setLoadingDue(true);
    try {
      const { data } = await api.get('/admin/invoices/due-subscriptions');
      setDueSubscriptions(data);
    } catch (error) {
      console.error('Error fetching due subscriptions:', error);
    } finally {
      setLoadingDue(false);
    }
  };

  const handleGenerateAllInvoices = async (isDryRun = true) => {
    setGeneratingAll(true);
    try {
      const { data } = await api.post('/admin/invoices/generate-all', {
        ...autoGenSettings,
        dry_run: isDryRun
      });
      setAutoGenResult(data);
      
      if (!isDryRun && data.total_generated > 0) {
        toast.success(`${data.total_generated} facture(s) générée(s)`);
        fetchInvoices();
        fetchDueSubscriptions();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setGeneratingAll(false);
    }
  };

  const handleGenerateSingleInvoice = async (subscriptionId) => {
    setGeneratingSingle(subscriptionId);
    try {
      const { data } = await api.post(`/admin/subscriptions/${subscriptionId}/generate-invoice`, {
        status: autoGenSettings.status,
        vat_rate: autoGenSettings.vat_rate,
        due_in_days: autoGenSettings.due_in_days
      });
      toast.success(`Facture ${data.invoice.invoice_number} générée`);
      fetchInvoices();
      fetchDueSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setGeneratingSingle(null);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchInvoices();
  };

  // Calculate totals from lines
  const calculateTotals = (lines, vatRate) => {
    const amountHT = lines.reduce((sum, line) => sum + (parseFloat(line.quantity || 0) * parseFloat(line.unit_price || 0)), 0);
    const vatAmount = amountHT * (parseFloat(vatRate) / 100);
    const amountTTC = amountHT + vatAmount;
    return { amountHT, vatAmount, amountTTC };
  };

  const totals = useMemo(() => calculateTotals(formData.lines, formData.vat_rate), [formData.lines, formData.vat_rate]);

  // Form handlers
  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { description: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      setFormData(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => i === index ? { ...line, [field]: value } : line)
    }));
  };

  // CRUD Operations
  const handleCreateInvoice = async () => {
    if (!formData.account_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    if (formData.lines.every(l => !l.description || l.unit_price <= 0)) {
      toast.error('Veuillez ajouter au moins une ligne valide');
      return;
    }
    
    try {
      const { data } = await api.post('/admin/invoices', {
        ...formData,
        amount_ht: totals.amountHT
      });
      toast.success('Facture créée avec succès');
      setCreateDialogOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleMarkAsSent = async (invoice) => {
    try {
      await api.post(`/admin/invoices/${invoice.id}/send`);
      toast.success('Facture marquée comme envoyée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedInvoice) return;
    
    try {
      await api.post(`/admin/invoices/${selectedInvoice.id}/pay`, paymentData);
      toast.success('Facture marquée comme payée');
      setPayDialogOpen(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleCancel = async () => {
    if (!selectedInvoice) return;
    
    try {
      await api.post(`/admin/invoices/${selectedInvoice.id}/cancel`, { reason: cancelReason });
      toast.success('Facture annulée');
      setCancelDialogOpen(false);
      setSelectedInvoice(null);
      setCancelReason('');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDelete = async (invoice) => {
    if (!confirm('Supprimer cette facture ?')) return;
    
    try {
      await api.delete(`/admin/invoices/${invoice.id}`);
      toast.success('Facture supprimée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleSendReminder = async (invoice) => {
    try {
      await api.post(`/admin/invoices/${invoice.id}/reminder`);
      toast.success('Relance enregistrée');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      account_id: '',
      issue_date: format(new Date(), 'yyyy-MM-dd'),
      due_date: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      vat_rate: 20,
      currency: 'MAD',
      lines: [{ description: '', quantity: 1, unit_price: 0 }],
      notes: '',
      client_notes: ''
    });
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const config = INVOICE_STATUS[status] || INVOICE_STATUS.DRAFT;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Facturation</h1>
          <p className="text-muted-foreground">Gestion des factures SaaS</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { fetchInvoices(); fetchDueSubscriptions(); }} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={() => {
              setAutoGenResult(null);
              setAutoGenDialogOpen(true);
              handleGenerateAllInvoices(true);
            }} 
            variant="outline"
            data-testid="auto-generate-btn"
          >
            <Zap className="w-4 h-4 mr-2" />
            Génération auto
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Facture
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <FileText className="w-4 h-4 mr-2" />
            Factures
          </TabsTrigger>
          <TabsTrigger value="auto-billing" data-testid="tab-auto-billing">
            <Zap className="w-4 h-4 mr-2" />
            Facturation auto
            {dueSubscriptions?.due_count > 0 && (
              <Badge className="ml-2 bg-orange-100 text-orange-700">{dueSubscriptions.due_count}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* INVOICES TAB */}
        <TabsContent value="invoices" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <SummaryCard
              title="Brouillons"
              count={summary.DRAFT?.count || 0}
              amount={summary.DRAFT?.total || 0}
              color="slate"
              icon={Edit}
              onClick={() => setStatusFilter('DRAFT')}
              active={statusFilter === 'DRAFT'}
            />
            <SummaryCard
              title="Envoyées"
              count={summary.SENT?.count || 0}
              amount={summary.SENT?.total || 0}
              color="blue"
              icon={Send}
              onClick={() => setStatusFilter('SENT')}
              active={statusFilter === 'SENT'}
            />
            <SummaryCard
              title="En retard"
              count={summary.OVERDUE?.count || 0}
              amount={summary.OVERDUE?.total || 0}
              color="red"
              icon={AlertTriangle}
              onClick={() => setStatusFilter('OVERDUE')}
              active={statusFilter === 'OVERDUE'}
            />
            <SummaryCard
              title="Payées"
              count={summary.PAID?.count || 0}
              amount={summary.PAID?.total || 0}
              color="green"
              icon={CheckCircle}
              onClick={() => setStatusFilter('PAID')}
              active={statusFilter === 'PAID'}
            />
            <SummaryCard
              title="Annulées"
              count={summary.CANCELLED?.count || 0}
              amount={summary.CANCELLED?.total || 0}
              color="gray"
              icon={X}
              onClick={() => setStatusFilter('CANCELLED')}
              active={statusFilter === 'CANCELLED'}
            />
          </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground">Rechercher</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Numéro, référence, client..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            
            <div className="w-[200px]">
              <Label className="text-xs text-muted-foreground">Client</Label>
              <Select value={accountFilter || "all"} onValueChange={(v) => { setAccountFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="account-filter">
                  <SelectValue placeholder="Tous les clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les clients</SelectItem>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={String(acc.id)}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-xs text-muted-foreground">Statut</Label>
              <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(INVOICE_STATUS).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={handleSearch} variant="secondary">
              <Filter className="w-4 h-4 mr-2" />
              Filtrer
            </Button>
            
            {(statusFilter || accountFilter || search) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setStatusFilter(''); setAccountFilter(''); setSearch(''); setPage(1); }}
              >
                Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune facture trouvée</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Numéro</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead className="text-right">Montant TTC</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(invoice => (
                  <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{invoice.account?.name}</p>
                        <p className="text-xs text-muted-foreground">{invoice.account?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>{format(new Date(invoice.issue_date), 'dd/MM/yyyy')}</TableCell>
                    <TableCell>
                      <span className={invoice.status === 'OVERDUE' ? 'text-red-600 font-medium' : ''}>
                        {format(new Date(invoice.due_date), 'dd/MM/yyyy')}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {parseFloat(invoice.amount_ttc).toLocaleString('fr-FR')} {invoice.currency}
                    </TableCell>
                    <TableCell>{renderStatusBadge(invoice.status)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/admin/billing/${invoice.id}`)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Voir détails
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${invoice.id}/pdf`, '_blank')}>
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger PDF
                          </DropdownMenuItem>
                          
                          {invoice.status === 'DRAFT' && (
                            <>
                              <DropdownMenuItem onClick={() => handleMarkAsSent(invoice)}>
                                <Send className="w-4 h-4 mr-2" />
                                Marquer envoyée
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(invoice)}>
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          {['SENT', 'OVERDUE'].includes(invoice.status) && (
                            <>
                              <DropdownMenuItem onClick={() => { setSelectedInvoice(invoice); setPayDialogOpen(true); }}>
                                <CreditCard className="w-4 h-4 mr-2" />
                                Marquer payée
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleSendReminder(invoice)}>
                                <Bell className="w-4 h-4 mr-2" />
                                Relancer ({invoice.reminder_count || 0})
                              </DropdownMenuItem>
                            </>
                          )}
                          
                          {invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => { setSelectedInvoice(invoice); setCancelDialogOpen(true); }}
                              >
                                <X className="w-4 h-4 mr-2" />
                                Annuler
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Page {pagination.page} sur {pagination.pages} ({pagination.total} factures)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.pages}
                onClick={() => setPage(p => p + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </Card>
      </TabsContent>

      {/* AUTO-BILLING TAB */}
      <TabsContent value="auto-billing" className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-blue-50">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dueSubscriptions?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Abonnements actifs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-50">
                  <AlertTriangle className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{dueSubscriptions?.due_count || 0}</p>
                  <p className="text-sm text-muted-foreground">À facturer maintenant</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Button 
                onClick={() => {
                  setAutoGenResult(null);
                  setAutoGenDialogOpen(true);
                  handleGenerateAllInvoices(true);
                }}
                className="w-full"
                size="lg"
                disabled={!dueSubscriptions?.due_count}
                data-testid="generate-all-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Générer toutes les factures dues
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Subscriptions List */}
        <Card>
          <CardHeader>
            <CardTitle>Abonnements et prochaines factures</CardTitle>
            <CardDescription>
              Liste des abonnements actifs avec leur statut de facturation
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loadingDue ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
              </div>
            ) : !dueSubscriptions?.subscriptions?.length ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucun abonnement actif</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Prix</TableHead>
                    <TableHead>Périodicité</TableHead>
                    <TableHead>Prochaine facture</TableHead>
                    <TableHead>Dernière facture</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dueSubscriptions.subscriptions.map(sub => (
                    <TableRow key={sub.subscription_id} data-testid={`subscription-row-${sub.subscription_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{sub.account_name}</p>
                          <p className="text-xs text-muted-foreground">{sub.account_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{sub.plan_name}</TableCell>
                      <TableCell className="font-medium">
                        {parseFloat(sub.monthly_price || 0).toLocaleString('fr-FR')} {sub.currency}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {sub.periodicity === 'MONTHLY' ? 'Mensuel' :
                           sub.periodicity === 'QUARTERLY' ? 'Trimestriel' :
                           sub.periodicity === 'YEARLY' ? 'Annuel' : sub.periodicity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {sub.next_billing_date && (
                          <span className={sub.is_due ? 'text-orange-600 font-medium' : ''}>
                            {format(new Date(sub.next_billing_date), 'dd/MM/yyyy')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {sub.last_invoice_date ? (
                          <div>
                            <span>{format(new Date(sub.last_invoice_date), 'dd/MM/yyyy')}</span>
                            <Badge className="ml-2" variant="outline">{sub.last_invoice_status}</Badge>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {sub.is_due ? (
                          <Badge className="bg-orange-100 text-orange-700">À facturer</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-700">À jour</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {sub.is_due && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleGenerateSingleInvoice(sub.subscription_id)}
                            disabled={generatingSingle === sub.subscription_id}
                            data-testid={`generate-invoice-${sub.subscription_id}`}
                          >
                            {generatingSingle === sub.subscription_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Play className="w-4 h-4 mr-1" />
                                Générer
                              </>
                            )}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Paramètres de génération
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>Statut initial</Label>
                <Select 
                  value={autoGenSettings.status} 
                  onValueChange={(v) => setAutoGenSettings(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DRAFT">Brouillon</SelectItem>
                    <SelectItem value="SENT">Envoyée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Taux TVA (%)</Label>
                <Input
                  type="number"
                  value={autoGenSettings.vat_rate}
                  onChange={(e) => setAutoGenSettings(prev => ({ ...prev, vat_rate: parseInt(e.target.value) || 20 }))}
                />
              </div>
              <div>
                <Label>Échéance (jours)</Label>
                <Input
                  type="number"
                  value={autoGenSettings.due_in_days}
                  onChange={(e) => setAutoGenSettings(prev => ({ ...prev, due_in_days: parseInt(e.target.value) || 30 }))}
                />
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={fetchDueSubscriptions}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </TabsContent>
      </Tabs>

      {/* Auto Generate Dialog */}
      <Dialog open={autoGenDialogOpen} onOpenChange={setAutoGenDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Génération automatique des factures</DialogTitle>
            <DialogDescription>
              Générer les factures pour tous les abonnements dus
            </DialogDescription>
          </DialogHeader>
          
          {generatingAll && !autoGenResult && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          )}
          
          {autoGenResult && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${autoGenResult.dry_run ? 'bg-blue-50' : 'bg-green-50'}`}>
                <p className={`font-medium ${autoGenResult.dry_run ? 'text-blue-800' : 'text-green-800'}`}>
                  {autoGenResult.message}
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{autoGenResult.total_processed}</p>
                  <p className="text-xs text-muted-foreground">Traités</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{autoGenResult.total_generated}</p>
                  <p className="text-xs text-muted-foreground">Générés</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold">{autoGenResult.total_skipped}</p>
                  <p className="text-xs text-muted-foreground">Ignorés</p>
                </div>
              </div>
              
              {autoGenResult.generated?.length > 0 && (
                <div className="max-h-40 overflow-y-auto">
                  <p className="text-sm font-medium mb-2">Factures {autoGenResult.dry_run ? 'à générer' : 'générées'}:</p>
                  <div className="space-y-1">
                    {autoGenResult.generated.map((g, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                        <span>{g.account_name}</span>
                        <span className="font-medium">
                          {g.invoice_number || `${parseFloat(g.amount || 0).toLocaleString('fr-FR')} MAD`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {autoGenResult.errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-1">Erreurs:</p>
                  {autoGenResult.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600">{e.account_name}: {e.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoGenDialogOpen(false)}>
              Fermer
            </Button>
            {autoGenResult?.dry_run && autoGenResult?.total_generated > 0 && (
              <Button 
                onClick={() => handleGenerateAllInvoices(false)} 
                disabled={generatingAll}
              >
                {generatingAll ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4 mr-2" />
                )}
                Confirmer la génération
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invoice Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle Facture</DialogTitle>
            <DialogDescription>Créer une facture pour un client SaaS</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Client Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Client *</Label>
                <Select 
                  value={formData.account_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, account_id: v }))}
                >
                  <SelectTrigger data-testid="invoice-account-select">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={String(acc.id)}>
                        {acc.name} {acc.company ? `(${acc.company})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Date d'émission</Label>
                <Input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, issue_date: e.target.value }))}
                />
              </div>
              
              <div>
                <Label>Date d'échéance</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
            </div>
            
            {/* Invoice Lines */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Lignes de facture</Label>
                <Button variant="outline" size="sm" onClick={addLine}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              
              <div className="space-y-2">
                {formData.lines.map((line, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      placeholder="Description"
                      value={line.description}
                      onChange={(e) => updateLine(index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qté"
                      value={line.quantity}
                      onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                      className="w-20"
                      min="1"
                    />
                    <Input
                      type="number"
                      placeholder="Prix HT"
                      value={line.unit_price}
                      onChange={(e) => updateLine(index, 'unit_price', e.target.value)}
                      className="w-28"
                      min="0"
                      step="0.01"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(index)}
                      disabled={formData.lines.length === 1}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Totals */}
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">Total HT</span>
                <span className="font-medium">{totals.amountHT.toLocaleString('fr-FR')} MAD</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-muted-foreground">TVA ({formData.vat_rate}%)</span>
                <span className="font-medium">{totals.vatAmount.toLocaleString('fr-FR')} MAD</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Total TTC</span>
                <span className="font-bold text-lg">{totals.amountTTC.toLocaleString('fr-FR')} MAD</span>
              </div>
            </div>
            
            {/* Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Notes internes</Label>
                <Textarea
                  placeholder="Notes internes (non visibles sur la facture)"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              <div>
                <Label>Notes client</Label>
                <Textarea
                  placeholder="Notes visibles sur la facture"
                  value={formData.client_notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_notes: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
              Annuler
            </Button>
            <Button onClick={handleCreateInvoice} data-testid="submit-invoice-btn">
              Créer la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Invoice Dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enregistrer le paiement</DialogTitle>
            <DialogDescription>
              Facture {selectedInvoice?.invoice_number} - {parseFloat(selectedInvoice?.amount_ttc || 0).toLocaleString('fr-FR')} MAD
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Mode de paiement</Label>
              <Select 
                value={paymentData.payment_method} 
                onValueChange={(v) => setPaymentData(prev => ({ ...prev, payment_method: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Référence de paiement</Label>
              <Input
                placeholder="N° de transaction, chèque..."
                value={paymentData.payment_reference}
                onChange={(e) => setPaymentData(prev => ({ ...prev, payment_reference: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Date de paiement</Label>
              <Input
                type="date"
                value={paymentData.paid_date}
                onChange={(e) => setPaymentData(prev => ({ ...prev, paid_date: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleMarkAsPaid} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmer le paiement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Invoice Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Annuler la facture</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. La facture {selectedInvoice?.invoice_number} sera marquée comme annulée.
            </DialogDescription>
          </DialogHeader>
          
          <div>
            <Label>Raison de l'annulation</Label>
            <Textarea
              placeholder="Raison de l'annulation..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Retour
            </Button>
            <Button variant="destructive" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Annuler la facture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ title, count, amount, color, icon: Icon, onClick, active }) => {
  const colorClasses = {
    slate: 'bg-slate-50 border-slate-200',
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    gray: 'bg-gray-50 border-gray-200'
  };
  
  const iconColors = {
    slate: 'text-slate-600',
    blue: 'text-blue-600',
    green: 'text-green-600',
    red: 'text-red-600',
    gray: 'text-gray-500'
  };

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${active ? 'ring-2 ring-violet-500' : ''} ${colorClasses[color]}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase">{title}</p>
            <p className="text-2xl font-bold">{count}</p>
            <p className="text-sm text-muted-foreground">{amount.toLocaleString('fr-FR')} MAD</p>
          </div>
          <Icon className={`w-8 h-8 ${iconColors[color]}`} />
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingPage;
